// ============================================================
// ShellErrorBoundary — wraps the entire MainframeShell.
//
// Uses Sentry's React boundary when initialized so unhandled
// renders are reported automatically. The fallback is a calm,
// shell-appropriate panel — never the gamey "Something went wrong".
// ============================================================

import type { ReactNode } from 'react'
import { SentryErrorBoundary, isInitialized } from './sentry'

interface Props {
  children: ReactNode
}

function ShellFallback() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#07090f',
        color: '#edf2ff',
        fontFamily: 'var(--font-body, "Inter", system-ui, sans-serif)',
        padding: 32,
      }}
    >
      <div
        style={{
          maxWidth: 420,
          padding: 28,
          borderRadius: 18,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: '#7f8db4',
            fontFamily: 'var(--font-mono, monospace)',
            letterSpacing: '0.18em',
            marginBottom: 8,
          }}
        >
          SQUARE CODE · SHELL ERROR
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>
          The mainframe stopped rendering.
        </div>
        <div style={{ fontSize: 13, color: '#97a7cf', lineHeight: 1.6 }}>
          The error has been reported. Reload the page to recover. If the issue
          persists, use the in-shell feedback button to add details.
        </div>
      </div>
    </div>
  )
}

export default function ShellErrorBoundary({ children }: Props) {
  if (!isInitialized()) {
    // Pre-init or test environment: render a plain pass-through.
    // We never want the boundary itself to error.
    return <PlainBoundary>{children}</PlainBoundary>
  }
  return (
    <SentryErrorBoundary fallback={<ShellFallback />} showDialog={false}>
      {children}
    </SentryErrorBoundary>
  )
}

// Tiny class boundary used when Sentry isn't initialized so
// non-Sentry callers still get a clean fallback.
import { Component, type ErrorInfo } from 'react'
class PlainBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(_error: Error, _info: ErrorInfo) { /* no-op */ }
  render() {
    if (this.state.error) return <ShellFallback />
    return this.props.children
  }
}
